import lazyWithRecovery from "./utils/lazyWithRecovery";
import React, { Suspense, useEffect, useRef } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import GuestOnlyRoute from "./routes/GuestOnlyRoute";
import PrivateRoute from "./routes/PrivateRoute";
import EmailConfirmedRoute from "./routes/EmailConfirmedRoute";
import { ConfirmProvider } from "../src/components/base/ConfirmProvider.jsx";
import { AuthProvider } from "./AuthContext";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { brandPalette } from "./brandPalette";
import { Box } from "@mui/material";
import { Outlet } from "react-router-dom";
import RouteSeoManager from "./seo/RouteSeoManager";
import PurchaseCompletionTracker from "./components/base/PurchaseCompletionTracker.jsx";
import UserAnalyticsTracker from "./components/base/UserAnalyticsTracker.jsx";
import LegacyCreateTravelTrackRedirect from "./routes/LegacyCreateTravelTrackRedirect.jsx";
import GuideScrollTop from "./components/01_appbase/GuideScrollTop";
import AppLoadingFallback, { finishAppStartup } from "./components/base/AppLoadingFallback";
import "./App.css";

const AppBaseRoutes = lazyWithRecovery(() => import("./routes/AppBaseRoutes"));
const UserRoute = lazyWithRecovery(() => import("./routes/UserRoutes"));
const VkConsumeCallbackRoute = lazyWithRecovery(() => import("./routes/VkAuthCallbackConsumeRoute.jsx"));
const LoginPage = lazyWithRecovery(() => import("./pages/02_user/LoginPage"));
const ConfirmEmailPage = lazyWithRecovery(() => import("./pages/02_user/ConfirmEmailPage"));
const CreateUser = lazyWithRecovery(() => import("./pages/02_user/CreateUserPage"));
const PasswordResetPage = lazyWithRecovery(() => import("./pages/02_user/PasswordResetPage"));
const PasswordChangePage = lazyWithRecovery(() => import("./pages/02_user/PasswordChangePage"));
const ErrorStatusPage = lazyWithRecovery(() => import("./pages/01_appbase/ErrorStatusPage"));
const TravelPlanningLandingPage = lazyWithRecovery(() => import("./pages/01_appbase/TravelPlanningLandingPage"));
const TravelPlanningGuidePage = lazyWithRecovery(() => import("./pages/01_appbase/TravelSeoContentPages").then((module) => ({ default: module.TravelPlanningGuidePage })));
const AiTravelPlannerPage = lazyWithRecovery(() => import("./pages/01_appbase/AiTravelPlannerDemoPage.jsx"));
const TravelStatisticsInfoPage = lazyWithRecovery(() => import("./pages/01_appbase/TravelSeoContentPages").then((module) => ({ default: module.TravelStatisticsInfoPage })));
const AboutTrackMePage = lazyWithRecovery(() => import("./pages/01_appbase/AboutTrackMePage.jsx"));
const DestinationGuidePage = lazyWithRecovery(() => import("./pages/01_appbase/DestinationGuidePage"));

const theme = createTheme({
    palette: brandPalette,
    typography: {
        fontFamily: `"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", Arial, sans-serif`,
    },
    components: {
        MuiModal: {
            styleOverrides: {
                root: {
                    "& > .MuiBox-root": {
                        boxSizing: "border-box",
                        border: "1px solid rgba(34, 49, 49, 0.16)",
                        borderRadius: "18px !important",
                        boxShadow: "0 18px 48px rgba(34, 49, 49, 0.18)",
                    },
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(34, 49, 49, 0.28) !important",
                        borderWidth: "1px !important",
                    },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(34, 49, 49, 0.42) !important",
                        borderWidth: "1px !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--green-color) !important",
                        borderWidth: "1px !important",
                    },
                    "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.Mui-error, & .MuiInputLabel-root.Mui-disabled": {
                        color: "var(--color-text-blue) !important",
                        WebkitTextFillColor: "var(--color-text-blue) !important",
                    },
                    "& .MuiInputBase-input, & .MuiSelect-select, & .MuiAutocomplete-input": {
                        color: "var(--color-text-blue)",
                        WebkitTextFillColor: "var(--color-text-blue)",
                    },
                    "& .MuiAutocomplete-inputRoot > span": {
                        color: "var(--color-text-blue) !important",
                    },
                    "& .MuiFormControlLabel-label, & .MuiCheckbox-root + .MuiTypography-root": {
                        color: "var(--color-text-blue)",
                    },
                    "& .MuiOutlinedInput-input": {
                        paddingLeft: "14px !important",
                        paddingRight: "14px !important",
                    },
                    "& .MuiSelect-select": {
                        height: "56px !important",
                        boxSizing: "border-box",
                        padding: "0 38px 0 14px !important",
                        whiteSpace: "nowrap !important",
                    },
                    "& textarea.MuiOutlinedInput-input": { padding: "14px !important" },
                    "& .MuiFilledInput-root": {
                        border: "1px solid rgba(34, 49, 49, 0.28)",
                        borderRadius: "10px",
                    },
                    "& .MuiFilledInput-root:hover": { borderColor: "rgba(34, 49, 49, 0.42)" },
                    "& .MuiFilledInput-root.Mui-focused": { borderColor: "var(--green-color)" },
                    "& .MuiInput-underline:before, & .MuiInput-underline:after": {
                        borderBottomWidth: "1px !important",
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    border: "1px solid rgba(34, 49, 49, 0.16)",
                    borderRadius: "18px",
                    boxShadow: "0 18px 48px rgba(34, 49, 49, 0.18)",
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    border: "1px solid rgba(34, 49, 49, 0.16)",
                    borderRadius: "10px",
                    boxShadow: "0 10px 28px rgba(34, 49, 49, 0.14)",
                    marginTop: "4px",
                },
                list: { padding: "6px" },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    border: "1px solid rgba(34, 49, 49, 0.16)",
                    borderRadius: "10px",
                    boxShadow: "0 10px 28px rgba(34, 49, 49, 0.14)",
                },
                option: {
                    borderRadius: 0,
                    margin: "2px 6px",
                    padding: "9px 12px",
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: "rgba(34, 49, 49, 0.46)",
                    "&.Mui-checked": { color: "var(--green-color)" },
                },
            },
        },
    },
});

function AppLayout() {
    const contentRef = useRef(null);
    const { pathname, search } = useLocation();
    useEffect(finishAppStartup, [pathname, search]);
    const publicPage = !/^\/userlk(?:\/|$)/.test(pathname);
    return (
        <Box
            ref={contentRef}
            sx={{
                minHeight: "100vh",
                backgroundColor: "var(--background-color-fon)",
            }}
            >
            <RouteSeoManager />
            <Outlet />
            {publicPage ? <GuideScrollTop key={pathname + search} rootRef={contentRef} /> : null}
        </Box>
    );
}


function App() {
    return (
        <AuthProvider>
            <PurchaseCompletionTracker />
            <UserAnalyticsTracker />
            <Suspense fallback={<AppLoadingFallback />}>
                <ThemeProvider theme={theme}>
                    <ConfirmProvider>
                        <Routes>
                            <Route element={<AppLayout />}>
                                {/* Гостевые (только для НЕавторизованных) */}
                                <Route element={<GuestOnlyRoute />}>
                                    <Route path="/userlogin" element={<LoginPage />} />
                                    <Route path="/register" element={<CreateUser />} />
                                    <Route path="/createuser" element={<Navigate to="/register" replace />} />
                                    <Route path="/signup" element={<Navigate to="/register" replace />} />
                                    <Route path="/passwordreset" element={<PasswordResetPage />} />
                                    <Route path="/reset-password/:uid/:token" element={<PasswordChangePage />} />
                                    <Route path="/auth/vk/callback" element={<VkConsumeCallbackRoute />} />
                                </Route>

                                {/* Подтверждение почты — доступно всем */}
                                <Route path="/confirm-email/:token" element={<ConfirmEmailPage />} />
                                <Route path="/error" element={<ErrorStatusPage />} />
                                <Route path="/planirovanie-puteshestviy/*" element={<TravelPlanningLandingPage />} />
                                <Route path="/kak-sostavit-plan-puteshestviya/*" element={<TravelPlanningGuidePage />} />
                                <Route path="/ii-planirovshchik-puteshestviy/*" element={<AiTravelPlannerPage />} />
                                <Route path="/statistika-puteshestviy/*" element={<TravelStatisticsInfoPage />} />
                                <Route path="/chto-posmotret/:destinationSlug/" element={<DestinationGuidePage />} />
                                <Route path="/o-trackme/" element={<AboutTrackMePage />} />

                                {/* Старые ссылки из писем: query-параметры сохраняются. */}
                                <Route
                                    path="/userlk/:userId/createtraveltrack"
                                    element={<LegacyCreateTravelTrackRedirect />}
                                />

                                {/* ЛК (скорее всего приватный) */}
                                <Route element={<PrivateRoute />}>
                                    <Route element={<EmailConfirmedRoute />}>
                                        <Route path="/userlk/:userId/*" element={<UserRoute />} />
                                    </Route>
                                </Route>
                                {/* Публичные маршруты */}
                                <Route path="/*" element={<AppBaseRoutes />} />
                            </Route>
                        </Routes>
                    </ConfirmProvider>
                </ThemeProvider>
            </Suspense>
        </AuthProvider>
    );
}

export default App;
